<template>
  <div class="custom-image-upload">
    <template v-if="!previewUrl">
      <CustomIcon
        useSymbol
        :type="iconType"
        :size="iconTypeSize"
        v-if="iconType"
        @click="handleClickUpload"
      ></CustomIcon>
      <div class="upload-area" v-else @click="handleClickUpload">
        <div class="upload-placeholder">
          <CustomIcon
            useSymbol
            type="shangchuantupian"
            size="22px"
          ></CustomIcon>
          <p>{{ uploadBtnTitle }}</p>
          <p class="tip">支持JPG/PNG/WEBP，大小不超过2MB</p>
        </div>
      </div>
    </template>
    <div class="preview-area" v-else>
      <img
        :src="previewUrl"
        alt="图片预览"
        class="preview-img"
        @click="handleClickUpload"
      />

      <div class="preview-actions">
        <n-button size="small" type="primary" @click="handleClickUpload"
          ><Custom-icon type="shangchuan"></Custom-icon
        ></n-button>
        <n-button size="small" type="error" @click="handleRemove">
          <Custom-icon type="shanchu"></Custom-icon
        ></n-button>
      </div>
    </div>
    <n-upload
      ref="uploadRef"
      v-bind="$attrs"
      :custom-request="handleCustomRequest"
      :before-upload="handleBeforeUpload"
      :accept="accept"
      style="display: none"
    />
    <upload-progress :loading="uploadLoading" :progress="uploadProgress" />
  </div>
</template>

<script setup lang="ts">
import { useFileStore } from '@/store/file/file'
import type { UploadFileInfo } from 'naive-ui'
import { computed, ref, watch } from 'vue'
import UploadProgress from './UploadProgress.vue'
import { createUploadLogic } from './utils/upload' // 引入封装的逻辑

// 1. 接收父组件透传的props（和upload.ts的UploadProps对齐）
const props = defineProps<{
  modelValue: any
  fileKey?: string | undefined
  isEdit?: boolean
  scene?: string | undefined
  uploadBtnTitle?: string | undefined
  maxFileSize?: number | undefined
  accept?: string | undefined
  isFileUrl?: boolean | undefined
  isFileId?: boolean | undefined
  iconType?: string
  iconTypeSize?: string
}>()

// 2. 定义事件（和upload.ts的emit对齐）
const emit = defineEmits<{
  'update:modelValue': [value: any]
  'upload-progress': [progress: number]
  success: [fileData: any]
  error: [file: UploadFileInfo]
  remove: [file: UploadFileInfo]
}>()

// 3. 初始化核心逻辑（复用upload.ts的createUploadLogic）
const fileStore = useFileStore()
const uploadRef = ref()
const uploadLoading = computed(() =>
  fileStore.getUploadLoadingByKey(props.fileKey)
)
// 核心：创建upload逻辑实例（所有上传逻辑都从这里来）
const uploadLogic = createUploadLogic(
  { ...props, multiple: false, uploadType: 'image' }, // 强制图片类型
  emit,
  fileStore
)
const uploadProgress = uploadLogic.uploadProgress
// 4. 同步状态：从uploadLogic的internalFileList推导预览URL
const previewUrl = computed(() => {
  const currentFile = uploadLogic.getCurrentFile()
  return currentFile?.url || props.modelValue || ''
})

// 5. 绑定核心方法（全部复用uploadLogic）
const handleBeforeUpload = (options: { file: UploadFileInfo }) => {
  return uploadLogic.handleBeforeUpload(options)
}

const handleCustomRequest = (options: any) => {
  return uploadLogic.handleCustomRequest(options)
}

const handleClickUpload = () => {
  // 复用upload.ts的触发上传逻辑
  uploadLogic.triggerUpload(uploadRef.value)
}

const handleRemove = async () => {
  const currentFile = uploadLogic.getCurrentFile()
  if (currentFile) {
    const uploadFileInfo: UploadFileInfo = {
      id: currentFile.id,
      name: currentFile.name,
      status: 'finished',
      url: currentFile.url
    }
    await uploadLogic.handleRemove({ file: uploadFileInfo })
  }
}

// 6. 监听上传进度/成功/失败（透传父组件）
watch(uploadLogic.uploadProgress, (progress) => {
  emit('upload-progress', progress)
})

// 暴露ref给父组件
defineExpose({
  uploadRef,
  clearUpload: uploadLogic.handleClear,
  getFileList: () => uploadLogic.internalFileList.value
})
</script>

<style scoped lang="scss">
/* 样式保留不变 */
.upload-area {
  width: 200px;
  height: 200px;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.3s;
}
.upload-area:hover {
  border-color: #409eff;
}
.upload-placeholder {
  text-align: center;
  color: #909399;
}
.upload-placeholder .tip {
  font-size: 12px;
  margin-top: 8px;
}
.preview-area {
  width: 200px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  .preview-img {
    width: 100%;
    height: 200px;
    object-fit: cover;
  }
  .preview-actions {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.1));
    padding: 8px;
    display: flex;
    justify-content: center;
    gap: 8px;
    opacity: 0;
    transition: opacity 0.3s;

    &:hover {
      opacity: 1;
    }
  }
}
/* .preview-actions {
  display: flex;
  gap: 8px;
  padding: 8px;
  background: #f5f7fa;
} */
</style>
